import { Link, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ArrowRight, FolderLock, Newspaper } from 'lucide-react';
import { useRooms, useUpdates } from '@client/lib/investor-api';
import { currentLocale } from '@client/lib/i18n';
import { fmtDate, fmtRelative } from '@client/lib/format';
import { pickLocale } from '@shared/types';
import { Badge } from '@client/components/ui/badge';
import { EmptyState, Skeleton } from '@client/components/ui/feedback';
import { MarkWhite } from '@client/components/brand/logo';
import { UpdateCard } from '@client/components/investor/update-card';
import type { Me } from '../index';

export const Route = createFileRoute('/_investor/rooms/')({ component: HomePage });

/** Investor home: the rooms shared with you and, across all of them, the latest updates from ResilientX. */
function HomePage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const { me } = Route.useRouteContext() as { me: Me };
  const rooms = useRooms();
  const feed = useUpdates({ limit: 8 });
  const firstName = me.name.split(/\s+/)[0] ?? me.name;
  const roomList = rooms.data?.rooms ?? [];
  return (
    <main className="mx-auto max-w-[1180px] px-5 py-8 md:px-8 md:py-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow mb-1">ResilientX · Investor Room</p>
          <h1 className="page-title">{t('home.welcome', { name: firstName })}</h1>
          <p className="mt-1 text-[14px] text-muted">{t('home.subtitle')}</p>
        </div>
      </div>

      <section className="mt-8" aria-labelledby="home-rooms">
        <div className="mb-3 flex items-center justify-between">
          <h2 id="home-rooms" className="section-title">{t('home.yourRooms')}</h2>
          {roomList.length > 0 ? <span className="text-[12.5px] text-muted">{t('home.roomCount', { count: roomList.length })}</span> : null}
        </div>
        {rooms.isPending ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-44" />)}</div>
        ) : roomList.length === 0 ? (
          <EmptyState icon={FolderLock} title={t('rooms.empty')} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {roomList.map((r, i) => (
              <Link key={r.id} to="/rooms/$slug" params={{ slug: r.slug }} className="group block overflow-hidden rounded-card bg-card no-underline shadow-card transition-shadow duration-150 hover:shadow-card-hover animate-reveal dark:border dark:border-border" style={{ animationDelay: `${i * 40}ms` }}>
                <div className="relative h-24 overflow-hidden">
                  {r.cover_url ? <img src={r.cover_url} alt="" className="size-full object-cover" /> : <div className="hero-navy flex size-full items-center justify-center"><MarkWhite size={32} /></div>}
                  {r.new_count > 0 ? <span className="absolute left-3 top-3 size-2 rounded-full bg-cyan ring-2 ring-white" aria-hidden /> : null}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-[15px] font-[700] leading-5 tracking-[-0.01em] text-foreground group-hover:text-link">{r.name}</h3>
                    {r.status === 'closed' ? <Badge tone="neutral">{t('rooms.closed')}</Badge> : r.nda_pending ? <Badge tone="warning">{t('rooms.ndaPending')}</Badge> : r.new_count > 0 ? <Badge tone="brand">{t('rooms.new', { count: r.new_count })}</Badge> : null}
                  </div>
                  {r.description ? <p className="mt-1 line-clamp-2 text-[13px] leading-5 text-muted">{pickLocale(r.description, locale)}</p> : null}
                  <p className="mt-3 text-[12.5px] text-muted">
                    {t('common.items', { count: r.item_count })} · {t('common.updated', { when: fmtRelative(r.updated_at, locale) })}
                    {r.expires_at ? ` · ${t('rooms.expires', { date: fmtDate(r.expires_at, locale) })}` : ''}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="mt-10" aria-labelledby="home-updates">
        <div className="mb-3 flex items-center justify-between">
          <h2 id="home-updates" className="section-title flex items-center gap-2">{t('home.latestUpdates')}{feed.data && feed.data.unread > 0 ? <Badge tone="good">{t('updates.unread', { count: feed.data.unread })}</Badge> : null}</h2>
          <Link to="/updates" search={{}} className="inline-flex items-center gap-1 text-[13px] font-[600] no-underline hover:underline">{t('home.allUpdates')}<ArrowRight className="size-3.5" /></Link>
        </div>
        {feed.isPending ? (
          <div className="grid gap-4 lg:grid-cols-2">{[0, 1].map((i) => <Skeleton key={i} className="h-40" />)}</div>
        ) : feed.data && feed.data.updates.length === 0 ? (
          <EmptyState icon={Newspaper} title={t('updates.empty')} body={t('updates.emptyBody')} compact />
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {feed.data?.updates.map((u, i) => <UpdateCard key={u.id} u={u} delay={i * 40} />)}
          </div>
        )}
      </section>
    </main>
  );
}
